import { useEffect, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { Sidebar, type SidebarMode } from '@/components/layout/Sidebar';
import { TopBar } from '@/components/layout/TopBar';
import { Breadcrumb } from '@/components/layout/Breadcrumb';
import { cn } from '@/lib/utils';
import { resolveBreadcrumb } from '@/config/routes';

const COLLAPSE_KEY = 'approvals.sidebar.collapsed';

function getViewportMode(): SidebarMode {
  if (typeof window === 'undefined') return 'full';
  const w = window.innerWidth;
  if (w < 768) return 'drawer';
  if (w < 1024) return 'rail';
  return 'full';
}

export interface AppShellProps {
  children: ReactNode;
  className?: string;
}

export function AppShell({ children, className }: AppShellProps) {
  const location = useLocation();
  const [mode, setMode] = useState<SidebarMode>(() => getViewportMode());
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem(COLLAPSE_KEY) === '1';
  });

  useEffect(() => {
    const onResize = () => {
      const next = getViewportMode();
      setMode(next);
      if (next !== 'drawer') setDrawerOpen(false);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(COLLAPSE_KEY, collapsed ? '1' : '0');
  }, [collapsed]);

  const segments = resolveBreadcrumb(location.pathname);
  const breadcrumb = segments.length > 0 ? <Breadcrumb segments={segments} /> : null;

  return (
    <div
      className={cn(
        'flex h-screen w-full overflow-hidden bg-neutral-50 text-neutral-800',
        className,
      )}
    >
      {mode !== 'drawer' && (
        <Sidebar
          mode={mode}
          collapsed={mode === 'full' ? collapsed : false}
          onCollapseToggle={mode === 'full' ? () => setCollapsed((c) => !c) : undefined}
        />
      )}
      <Sidebar
        mode="drawer"
        open={mode === 'drawer' && drawerOpen}
        onClose={() => setDrawerOpen(false)}
      />
      <div className="flex min-w-0 flex-1 flex-col">
        <TopBar
          breadcrumb={breadcrumb}
          showHamburger={mode === 'drawer'}
          onOpenDrawer={() => setDrawerOpen(true)}
        />
        <main className="flex-1 overflow-y-auto bg-neutral-50">{children}</main>
      </div>
    </div>
  );
}
